import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { APP_ICON, INSTALL_PATH, useInstall } from '@/shared/pwa';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import GhostLoader from '@/components/GhostLoader';
import { cn } from '@/lib/utils';
import { useSeo } from '@/shared/seo';
import { useT } from '@/shared/i18n';
import { I } from '@/shared/locales/install/keys';
import { P, around } from '@/shared/locales/pages/keys';

export const route = { path: '/install', layout: 'public', access: 'public' };

/** Every text value below is an English translation key. */
const PERKS = [
  { icon: 'AppWindow', title: I.p1t, text: I.p1 },
  { icon: 'KeyRound', title: I.p2t, text: I.p2 },
  { icon: 'Download', title: I.p3t, text: I.p3 },
];

const PLATFORMS = [
  {
    key: 'ios',
    label: I.lIos,
    icon: 'Smartphone',
    browser: 'Safari',
    steps: [
      { icon: 'Compass', title: I.i1t, text: I.i1 },
      { icon: 'Share', title: I.i2t, text: I.i2 },
      { icon: 'SquarePlus', title: I.i3t, text: I.i3 },
      { icon: 'Check', title: I.i4t, text: I.i4 },
    ],
    tip: I.iTip,
  },
  {
    key: 'android',
    label: 'Android',
    icon: 'TabletSmartphone',
    browser: 'Chrome',
    steps: [
      { icon: 'Globe', title: I.a1t, text: I.a1 },
      { icon: 'EllipsisVertical', title: I.a2t, text: I.a2 },
      { icon: 'SquarePlus', title: I.a3t, text: I.a3 },
      { icon: 'Check', title: I.a4t, text: I.a4 },
    ],
    tip: I.aTip,
  },
  {
    key: 'desktop',
    label: I.lDesk,
    icon: 'Monitor',
    browser: I.bDesk,
    steps: [
      { icon: 'Globe', title: I.d1t, text: I.d1 },
      { icon: 'MonitorDown', title: I.d2t, text: I.d2 },
      { icon: 'Check', title: I.d3t, text: I.d3 },
    ],
    tip: I.dTip,
  },
  {
    key: 'mac',
    label: 'Mac',
    icon: 'Laptop',
    browser: I.bMac,
    steps: [
      { icon: 'Compass', title: I.i1t, text: I.m1 },
      { icon: 'Share', title: I.m2t, text: I.m2 },
      { icon: 'Check', title: I.m3t, text: I.m3 },
    ],
    tip: I.mTip,
  },
];

const QUESTIONS = [
  { q: I.fq1, a: I.fa1 },
  { q: I.fq2, a: I.fa2 },
  { q: I.fq3, a: I.fa3 },
  { q: I.fq4, a: I.fa4 },
  { q: I.fq5, a: I.fa5 },
];

/** TV setup through the Downloader app. The code is given at purchase (or the customer uses their own), so it shows blank here. */
const TV_DEVICES = ['Fire TV Stick', 'Fire TV Cube', 'Android TV', 'Google TV', 'Android TV boxes'];

const DOWNLOADER_STEPS = [
  { icon: 'Store', title: I.s1t, text: I.s1 },
  { icon: 'ShieldCheck', title: I.s2t, text: I.s2, tip: I.s2tip },
  { icon: 'KeyRound', title: I.s3t, text: I.s3, code: true },
  { icon: 'Download', title: I.s4t, text: I.s4 },
  { icon: 'Tv', title: I.s5t, text: I.s5 },
];

function CodeSlot({ size = 'md' }) {
  const { t } = useT();
  return (
    <div
      className={cn(
        'flex rounded-md border border-dashed border-primary/50 bg-primary/5',
        size === 'lg'
          ? 'flex-col items-start gap-3 px-4 py-4'
          : 'mt-3 flex-wrap items-center justify-between gap-3 px-3 py-2.5',
      )}
    >
      <span className="flex gap-1.5" aria-hidden="true">
        {Array.from({ length: 7 }, (_, i) => (
          <span
            key={i}
            className={cn(
              'block rounded-sm border-b-2 border-muted-foreground/40',
              size === 'lg' ? 'h-8 w-5 sm:w-6' : 'h-5 w-3.5',
            )}
          />
        ))}
      </span>
      <span className="rounded-sm bg-primary/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-primary">
        {t(I.givenOrOwn)}
      </span>
    </div>
  );
}

function DownloaderSetup() {
  const { t } = useT();
  const [headPre, headPost] = around(t(I.tvHead), 'code');
  return (
    <section id="tv-setup" className={cn(WRAP, 'scroll-mt-24 pt-28')}>
      <p className={cn(LABEL, 'flex items-center gap-2')}>
        <ApperIcon name="Tv" size={12} />
        {t(I.setupTv)}
      </p>
      <h2 className="mt-4 max-w-3xl text-[clamp(32px,4.5vw,54px)] font-light leading-[1.05]">
        {headPre}
        <span className={GLOW}>{t(I.tvCode)}</span>
        {headPost}
      </h2>
      <p className="mt-4 max-w-2xl text-[15px] leading-7 text-muted-foreground">
        {t(I.tvFor, { list: TV_DEVICES.slice(0, -1).join(', '), last: TV_DEVICES[TV_DEVICES.length - 1] })}
      </p>

      <div className="mt-10 grid gap-8 md:grid-cols-[minmax(0,1fr)_19rem] md:gap-12">
        <ol className="space-y-px overflow-hidden rounded-lg border border-border bg-border">
          {DOWNLOADER_STEPS.map((s, i) => (
            <li key={s.title} className="flex gap-4 bg-background p-5 md:p-6">
              <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-heading text-xl text-primary">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex items-center gap-2 text-lg">
                  {t(s.title)}
                  <ApperIcon name={s.icon} size={16} className="shrink-0 text-muted-foreground" />
                </p>
                <p className="mt-1 text-[15px] leading-7 text-muted-foreground">{t(s.text)}</p>
                {s.code && (
                  <div className="max-w-sm">
                    <CodeSlot />
                    <p className="mt-2 text-xs text-muted-foreground">{t(I.leftBlank)}</p>
                  </div>
                )}
                {s.tip && (
                  <p className="mt-3 flex gap-2 rounded-md bg-muted px-3 py-2 text-sm leading-6 text-muted-foreground">
                    <ApperIcon name="Lightbulb" size={16} className="mt-0.5 shrink-0 text-primary" />
                    {t(s.tip)}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>

        <aside className="space-y-4">
          <div className="rounded-lg border border-border bg-card p-5 text-card-foreground md:sticky md:top-24">
            <p className={LABEL}>{t(I.yourCode)}</p>
            <div className="mt-3">
              <CodeSlot size="lg" />
            </div>
            <p className="mt-4 text-sm leading-6 text-foreground">{t(I.whenPurchase)}</p>
            <p className="mt-1 text-sm leading-6 text-muted-foreground">{t(I.needStep3)}</p>
            <div className="mt-5 flex flex-wrap gap-2">
              <Button size="sm" asChild>
                <Link to={{ pathname: '/', hash: '#plans' }}>
                  <ApperIcon name="Ghost" size={16} />
                  {t(I.getCode)}
                </Link>
              </Button>
              <Button size="sm" variant="outline" asChild>
                <Link to="/customer/login">{t('Sign in')}</Link>
              </Button>
            </div>
          </div>
          <div className="flex gap-3 rounded-lg border border-dashed border-highlight/40 bg-highlight/5 p-4">
            <ApperIcon name="Info" size={18} className="mt-0.5 shrink-0 text-highlight" />
            <p className="text-sm leading-6 text-muted-foreground">{t(I.onlyAndroid)}</p>
          </div>
        </aside>
      </div>
    </section>
  );
}

function Steps({ platform }) {
  const { t } = useT();
  return (
    <div className="grid gap-8 md:grid-cols-[minmax(0,1fr)_17rem] md:gap-12">
      <ol className="space-y-px overflow-hidden rounded-lg border border-border bg-border">
        {platform.steps.map((s, i) => (
          <li key={s.title} className="flex gap-4 bg-background p-5 md:p-6">
            <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-heading text-xl text-primary">
              {i + 1}
            </span>
            <div className="min-w-0">
              <p className="flex items-center gap-2 text-lg">
                {t(s.title)}
                <ApperIcon name={s.icon} size={16} className="shrink-0 text-muted-foreground" />
              </p>
              <p className="mt-1 text-[15px] leading-7 text-muted-foreground">{t(s.text)}</p>
            </div>
          </li>
        ))}
      </ol>
      <aside className="space-y-4">
        <div className="rounded-lg border border-border bg-card p-5 text-card-foreground">
          <p className={LABEL}>{t(I.use)}</p>
          <p className="mt-2 text-lg">{t(platform.browser)}</p>
          {platform.tip && <p className="mt-3 text-sm leading-6 text-muted-foreground">{t(platform.tip)}</p>}
        </div>
        <div className="flex items-center gap-3 rounded-lg border border-dashed border-highlight/40 bg-highlight/5 p-4">
          <img src={APP_ICON} alt="" width={48} height={48} className="size-12 rounded-xl" />
          <p className="text-sm text-muted-foreground">{t(I.lookIcon)}</p>
        </div>
      </aside>
    </div>
  );
}

function InstallAction({ install }) {
  const { t } = useT();
  const [busy, setBusy] = useState(false);

  async function run() {
    setBusy(true);
    try {
      const outcome = await install.prompt();
      if (outcome === 'accepted') toast.success(t(I.installed));
    } finally {
      setBusy(false);
    }
  }

  async function copyLink() {
    const url = `${window.location.origin}${INSTALL_PATH}`;
    try {
      if (navigator.share && install.platform !== 'desktop' && install.platform !== 'mac') {
        await navigator.share({ title: t(I.installGC), url });
        return;
      }
      await navigator.clipboard.writeText(url);
      toast.success(t(I.copied));
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error(t(I.cantCopy, { url }));
    }
  }

  if (install.installed) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <span className="inline-flex items-center gap-2 rounded-full border border-success-border bg-success-muted px-4 py-2 text-sm text-success">
          <ApperIcon name="CircleCheck" size={16} />
          {t(I.usingApp)}
        </span>
        <Button variant="outline" onClick={copyLink}>
          <ApperIcon name="Send" size={16} />
          {t(I.sendOther)}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      {install.canPrompt ? (
        <Button size="lg" onClick={run} disabled={busy}>
          {busy ? <GhostLoader compact size={18} /> : <ApperIcon name="Download" size={18} />}
          {t(I.installGC)}
        </Button>
      ) : (
        <Button size="lg" asChild>
          <a href="#steps">
            <ApperIcon name="ListOrdered" size={18} />
            {t(I.showHow)}
          </a>
        </Button>
      )}
      <Button size="lg" variant="outline" onClick={copyLink}>
        <ApperIcon name="Send" size={18} />
        {t(I.sendPhone)}
      </Button>
      <Button size="lg" variant="ghost" asChild>
        <a href="#tv-setup">
          <ApperIcon name="Tv" size={18} />
          {t(I.setupFire)}
        </a>
      </Button>
    </div>
  );
}

export default function Install() {
  const { t } = useT();
  const install = useInstall();
  const [tab, setTab] = useState(install.platform);
  const [heroPre, heroPost] = around(t(I.heroA), 'rest');

  useSeo({
    title: 'Install the Ghost Cable app',
    description:
      'Install Ghost Cable as an app on iPhone, iPad, Android, Windows, Mac or Chromebook, and set up Fire TV or Android TV with the Downloader code you get when you purchase, or your own.',
  });

  return (
    <div className="overflow-x-hidden pb-24">
      <section className={cn(WRAP, 'pt-16 md:pt-24')}>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/">
            <ApperIcon name="ArrowLeft" size={16} className="rtl:rotate-180" />
            {t(P.back)}
          </Link>
        </Button>
        <div className="mt-10 grid items-center gap-12 md:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <div>
            <p className={cn(LABEL, 'flex items-center gap-2')}>
              <ApperIcon name="Smartphone" size={12} />
              {t(I.getApp)}
            </p>
            <h1 className="mt-4 text-[clamp(40px,6.5vw,76px)] font-light leading-[1.02]">
              {heroPre}
              <span className={GLOW}>{t(I.heroB)}</span>
              {heroPost}
            </h1>
            <p className="mt-5 max-w-xl text-[17px] leading-8 text-muted-foreground">{t(I.heroP)}</p>
            <div className="mt-8">
              <InstallAction install={install} />
            </div>
            {install.inAppBrowser && !install.installed && (
              <p className="mt-5 flex max-w-xl gap-2 rounded-lg border border-warning-border bg-warning-muted px-4 py-3 text-sm text-foreground">
                <ApperIcon name="TriangleAlert" size={16} className="mt-0.5 shrink-0 text-warning" />
                {t(I.inApp)}
              </p>
            )}
          </div>
          <div className="relative mx-auto w-full max-w-xs" aria-hidden="true">
            <div className="absolute inset-6 rounded-full bg-primary/20 blur-3xl" />
            <div className="relative rounded-[2.25rem] border border-border bg-card p-3 shadow-lg">
              <div className="rounded-[1.75rem] border border-border bg-background px-6 pb-10 pt-8">
                <div className="grid grid-cols-4 gap-4">
                  {Array.from({ length: 11 }, (_, i) => (
                    <span key={i} className="aspect-square rounded-xl bg-muted" />
                  ))}
                  <span className="relative">
                    <img src={APP_ICON} alt="" className="aspect-square w-full rounded-xl ring-2 ring-primary/60" />
                  </span>
                </div>
                <p className="mt-8 text-center text-sm text-muted-foreground">
                  <span className="text-primary">Ghost Cable</span> · {t(I.nextTo)}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className={cn(WRAP, 'pt-24')}>
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-3">
          {PERKS.map((p) => (
            <div key={p.title} className="bg-background p-6 md:p-8">
              <ApperIcon name={p.icon} size={22} className="text-primary" />
              <p className="mt-4 text-lg">{t(p.title)}</p>
              <p className="mt-1 text-sm leading-6 text-muted-foreground">{t(p.text)}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="steps" className={cn(WRAP, 'scroll-mt-24 pt-28')}>
        <p className={LABEL}>{t('How to install')}</p>
        <h2 className="mt-4 max-w-3xl text-[clamp(32px,4.5vw,54px)] font-light leading-[1.05]">{t(I.pickDevice)}</h2>
        <Tabs value={tab} onValueChange={setTab} className="mt-10 gap-8">
          <div className="overflow-x-auto pb-1">
            <TabsList className="h-auto min-w-max">
              {PLATFORMS.map((p) => (
                <TabsTrigger key={p.key} value={p.key} className="px-3 py-2">
                  <ApperIcon name={p.icon} size={16} />
                  {t(p.label)}
                  {p.key === install.platform && (
                    <span className="rounded-sm bg-primary/15 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-primary">
                      {t(I.thisDevice)}
                    </span>
                  )}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          {PLATFORMS.map((p) => (
            <TabsContent key={p.key} value={p.key}>
              <Steps platform={p} />
            </TabsContent>
          ))}
        </Tabs>
      </section>

      <DownloaderSetup />

      <section className={cn(WRAP, 'pt-28')}>
        <div className="grid gap-12 md:grid-cols-[1fr_1.6fr]">
          <div>
            <p className={LABEL}>{t(I.goodToKnow)}</p>
            <h2 className="mt-4 text-[clamp(30px,4vw,48px)] font-light leading-[1.05]">{t(I.before)}</h2>
          </div>
          <Accordion type="single" collapsible defaultValue="q-0" className="border-t border-border">
            {QUESTIONS.map((f, i) => (
              <AccordionItem key={f.q} value={`q-${i}`} className="border-border">
                <AccordionTrigger className="py-5 text-start text-lg font-normal hover:text-primary hover:no-underline">
                  {t(f.q)}
                </AccordionTrigger>
                <AccordionContent className="max-w-2xl pb-6 text-[15px] leading-7 text-muted-foreground">
                  {t(f.a)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className={cn(WRAP, 'pt-28')}>
        <div className="flex flex-col gap-6 rounded-lg border border-border bg-card p-6 text-card-foreground md:flex-row md:items-center md:justify-between md:p-10">
          <div>
            <p className={LABEL}>{t(I.notCustomer)}</p>
            <h2 className="mt-3 font-heading text-4xl">{t(I.pickPlan)}</h2>
            <p className="mt-2 max-w-md text-sm text-muted-foreground">{t(I.onceLive)}</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button size="lg" asChild>
              <Link to={{ pathname: '/', hash: '#plans' }}>{t(I.seePlans)}</Link>
            </Button>
            <Button size="lg" variant="outline" asChild>
              <Link to="/customer/login">{t('Sign in')}</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
